import { TextInput } from '@consigli/facade';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TopicDropdownFooterProps {
  /** Whether the parent dropdown is open; used to reset the input when it closes. */
  isOpen: boolean;
  onAddCustomTopic: (topic: string) => void;
}

/**
 * Shared "Add custom topic" footer rendered in both individual and bulk edit
 * contexts. Meant to be passed to DropdownMenu's `footer` prop so it appears
 * below the divider. Toggles between a button and a text input, committing the
 * trimmed value on Enter and cancelling on Escape.
 */
export const TopicDropdownFooter: FC<TopicDropdownFooterProps> = ({ isOpen, onAddCustomTopic }) => {
  const { t } = useTranslation();
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customTopicText, setCustomTopicText] = useState('');

  const customInputRef = useCallback((el: HTMLInputElement | null) => {
    el?.focus();
  }, []);

  // Reset the custom-topic input whenever the dropdown closes so it reopens clean.
  useEffect(() => {
    if (!isOpen) {
      setShowCustomInput(false);
      setCustomTopicText('');
    }
  }, [isOpen]);

  if (showCustomInput) {
    return (
      <div className="px-1">
        <TextInput
          ref={customInputRef}
          value={customTopicText}
          onChange={(e) => setCustomTopicText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && customTopicText.trim()) {
              e.preventDefault();
              onAddCustomTopic(customTopicText.trim());
              setShowCustomInput(false);
              setCustomTopicText('');
            } else if (e.key === 'Escape') {
              setShowCustomInput(false);
              setCustomTopicText('');
            }
          }}
          className="w-full text-sm"
          placeholder={t('meeting-secretary.action-items.enter-topic', 'Enter topic name')}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => {
        setShowCustomInput(true);
        setCustomTopicText('');
      }}
      className="w-full text-left px-3 py-2 text-sm text-brand hover:bg-gray-50 transition-colors font-medium"
    >
      + {t('meeting-secretary.action-items.add-custom-topic', 'Add custom topic')}
    </button>
  );
};
